/**
 * useSandboxConfigs — React hooks for sandbox configuration CRUD.
 *
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  listSandboxConfigurations,
  createOrUpdateSandboxConfiguration,
  deleteSandboxConfiguration,
  getSandboxConfiguration,
} from '@/services/sandboxService';
import type {
  SandboxConfiguration,
  PutSandboxConfigurationRequest,
} from '@/types/sandbox';
import { SandboxProvisioningState } from '@/types/sandbox';

export function useSandboxConfigs(options?: { poll?: boolean }) {
  const { getOttoToken } = useAuth();
  const selectedProject = useAppStore((s) => s.selectedProject);
  const poll = options?.poll ?? false;

  return useQuery({
    queryKey: ['sandboxconfigs', selectedProject],
    queryFn: async () => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      return listSandboxConfigurations(token, selectedProject!);
    },
    enabled: !!selectedProject,
    staleTime: 1000 * 60 * 5, // 5 minutes
    // Poll every 10s while any sandbox is still provisioning (opt-in)
    refetchInterval: poll
      ? (query) => {
          const data = query.state.data;
          if (data?.some((s) => s.properties.provisioningState === SandboxProvisioningState.InProgress)) {
            return 10_000;
          }
          return false;
        }
      : false,
  });
}

export function useSandboxConfig(sandboxConfigName: string) {
  const { getOttoToken } = useAuth();
  const selectedProject = useAppStore((s) => s.selectedProject);

  return useQuery({
    queryKey: ['sandboxconfigs', selectedProject, sandboxConfigName],
    queryFn: async (): Promise<SandboxConfiguration | null> => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      return getSandboxConfiguration(token, selectedProject!, sandboxConfigName);
    },
    enabled: !!selectedProject,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useSaveSandboxConfig() {
  const { getOttoToken } = useAuth();
  const selectedProject = useAppStore((s) => s.selectedProject);
  const queryClient = useQueryClient();

  return useMutation<
    SandboxConfiguration,
    Error,
    { sandboxConfigName: string; request: PutSandboxConfigurationRequest }
  >({
    mutationFn: async ({ sandboxConfigName, request }) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      return createOrUpdateSandboxConfiguration(token, selectedProject!, sandboxConfigName, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sandboxconfigs'] });
    },
  });
}

/**
 * Rebuild an existing sandbox configuration without re-authenticating.
 *
 * Reuses the existing connection by fetching the current configuration and
 * re-submitting its payload (including the existing `connectionId`) via PUT.
 * Every PUT triggers a build on the backend; if a build is already in progress
 * the backend returns 409 Conflict which surfaces as a user-friendly message.
 */
export function useRebuildSandboxConfig() {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<SandboxConfiguration, Error, { sandboxConfigName: string; projectName: string }>({
    mutationFn: async ({ sandboxConfigName, projectName }) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');

      // Fetch the current configuration so we re-submit the existing payload —
      // including the existing connectionId — rather than creating a new connection.
      const existing = await getSandboxConfiguration(token, projectName, sandboxConfigName);
      if (!existing) {
        throw new Error(`Sandbox configuration "${sandboxConfigName}" was not found.`);
      }

      const request: PutSandboxConfigurationRequest = {
        properties: {
          ...(existing.properties.repositorySettings && {
            repositorySettings: existing.properties.repositorySettings,
          }),
          ...(existing.properties.resourceSettings && {
            resourceSettings: existing.properties.resourceSettings,
          }),
        },
      };

      return createOrUpdateSandboxConfiguration(token, projectName, sandboxConfigName, request);
    },
    onSuccess: (updated, { sandboxConfigName, projectName }) => {
      // Every PUT triggers a build, so the row should show InProgress
      // immediately. We can't rely on the PUT response body to carry
      // provisioningState: InProgress — in deployed environments it may omit
      // it or echo back the previously stored terminal state (e.g. Failed).
      // So overlay InProgress explicitly (and clear the stale errorMessage)
      // rather than invalidating, which would refetch the LIST endpoint and
      // race the backend. The existing list poll (active while any sandbox is
      // InProgress) then reconciles to Ready/Failed as it progresses.
      // Match the row on the name we requested (not the response body's name)
      // so the update lands regardless of what the backend echoes back.
      const overlay = (props: SandboxConfiguration['properties']): SandboxConfiguration['properties'] => {
        // Drop any stale errorMessage from the prior terminal state.
        const { errorMessage: _staleError, ...rest } = { ...props, ...updated.properties };
        return {
          ...rest,
          provisioningState: SandboxProvisioningState.InProgress,
        };
      };
      queryClient.setQueryData<SandboxConfiguration[]>(
        ['sandboxconfigs', projectName],
        (old) =>
          old?.map((s) =>
            s.name === sandboxConfigName ? { ...s, ...updated, name: s.name, properties: overlay(s.properties) } : s,
          ),
      );
      queryClient.setQueryData<SandboxConfiguration | null>(
        ['sandboxconfigs', projectName, sandboxConfigName],
        (old) =>
          old
            ? { ...old, ...updated, name: old.name, properties: overlay(old.properties) }
            : old,
      );
    },
  });
}

export function useDeleteSandboxConfig() {
  const { getOttoToken } = useAuth();
  const selectedProject = useAppStore((s) => s.selectedProject);
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  return useMutation<void, Error, string>({
    mutationFn: async (sandboxConfigName) => {
      const token = await getOttoToken();
      if (!token) throw new Error('Failed to get access token');
      await deleteSandboxConfiguration(token, selectedProject!, sandboxConfigName);
    },
    onSuccess: (_data, sandboxConfigName) => {
      queryClient.invalidateQueries({ queryKey: ['sandboxconfigs'] });
      addNotification({
        type: 'success',
        title: 'Sandbox configuration deleted',
        message: `Sandbox configuration "${sandboxConfigName}" has been deleted.`,
      });
    },
    onError: (error, sandboxName) => {
      addNotification({
        type: 'error',
        title: 'Sandbox configuration deletion failed',
        message: `Failed to delete sandbox configuration "${sandboxName}": ${error.message}`,
      });
    },
  });
}
